import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getListings } from 'domains/listing/services/listingService';
import StaticMap from 'components/map/components/StaticMap';

const CATEGORY_LABELS = {
  cars: 'Cars',
  motorcycles: 'Motorcycles',
  furniture: 'Furniture',
  removals: 'Removals',
  pallets: 'Pallets',
  freight: 'Freight',
  parcels: 'Parcels',
  machinery: 'Machinery',
  other_vehicles: 'Other Vehicles',
  pets: 'Pets',
  building_materials: 'Building Materials',
  fragile: 'Fragile',
  boats: 'Boats',
  bicycles: 'Bicycles',
  vehicle_parts: 'Vehicle Parts',
  household: 'Household',
  other: 'Other',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return null;
  }
}

function formatPrice(price) {
  if (price == null || !Number.isFinite(Number(price))) return null;
  return `€${Number(price).toLocaleString('en-EU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDistance(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  return `${Math.round(Number(km)).toLocaleString('en')} km`;
}

export function ListingCard({ listing, role, navigate, onTriggerMessage }) {
  const listingId = listing.id || listing._id;
  const humanId = listing.humanId || null;
  const from = listing.fromCity || listing.fromAddress || '—';
  const to = listing.toCity || listing.toAddress || '—';
  const category = CATEGORY_LABELS[listing.category] || listing.category || 'Other';
  const pickup = formatDate(listing.pickupDateFrom);
  const price = formatPrice(listing.clientPriceGross);
  const distance = formatDistance(listing.distanceKm);
  const status = listing.status || 'open';
  const hasMap = listing.publicFromCoords || listing.publicToCoords;

  const handleView = () => {
    if (role === 'partner') {
      navigate(`/listings/${listingId}`);
    } else {
      navigate(`/my-listings/${listingId}`);
    }
  };

  const handleQuote = () => {
    if (onTriggerMessage) {
      onTriggerMessage(`Submit a quote for listing ${listingId}`, { listingId, entityId: listingId, entityType: 'listing' });
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="xl" bg="white" borderColor="gray.200" overflow="hidden">
      {hasMap ? (
        <Box h="110px">
          <StaticMap
            origin={listing.publicFromCoords}
            destination={listing.publicToCoords}
            width={400}
            height={110}
          />
        </Box>
      ) : null}
      <Stack spacing={2} p={3}>
        <HStack justify="space-between" align="flex-start">
          <Stack spacing={0} flex={1}>
            <Text fontSize="sm" fontWeight="700" color="navy.700" noOfLines={1}>
              {from} → {to}
            </Text>
            {humanId ? (
              <Text fontSize="xs" color="gray.400" fontFamily="mono">{humanId}</Text>
            ) : null}
            {pickup ? (
              <Text fontSize="xs" color="gray.500">Pickup: {pickup}</Text>
            ) : null}
          </Stack>
          <Badge colorScheme="purple" borderRadius="full" px={2} py={0.5} fontSize="10px" flexShrink={0}>
            {category}
          </Badge>
        </HStack>

        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            {price ? (
              <Text fontSize="sm" fontWeight="700" color="blue.600">{price}</Text>
            ) : (
              <Text fontSize="xs" color="gray.400" fontStyle="italic">Price on quote</Text>
            )}
            {distance ? (
              <Text fontSize="xs" color="gray.500">{distance}</Text>
            ) : null}
          </HStack>
          <Badge colorScheme={status === 'open' ? 'blue' : 'gray'} borderRadius="full" px={2} fontSize="10px">
            {status.replace(/_/g, ' ')}
          </Badge>
        </HStack>

        <HStack spacing={2}>
          <Button size="xs" colorScheme="blue" onClick={handleView}>
            View listing
          </Button>
          {role === 'partner' && status === 'open' ? (
            <Button size="xs" variant="outline" colorScheme="blue" onClick={handleQuote}>
              Submit quote
            </Button>
          ) : null}
        </HStack>
      </Stack>
    </Box>
  );
}

export default function AiListingCards({ result, role, navigate, onTriggerMessage }) {
  const [listings, setListings] = useState(result.listings || []);
  const [page, setPage] = useState(result.context?.page || 1);
  const [hasMore, setHasMore] = useState(result.context?.hasMore || false);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleShowMore = async () => {
    if (!result.context?.filters || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await getListings({ ...result.context.filters, page: nextPage, limit: 5 });
      const nextListings = response.listings || response.data || response || [];
      setListings((prev) => [...prev, ...nextListings]);
      setPage(nextPage);
      const total = response.meta?.total ?? response.total ?? null;
      setHasMore(total != null ? listings.length + nextListings.length < total : nextListings.length >= 5);
    } catch {
      // silent fail
    } finally {
      setLoadingMore(false);
    }
  };

  if (!listings.length) {
    return (
      <Box borderWidth="1px" borderRadius="xl" p={3} bg="gray.50" borderColor="gray.200">
        <Text fontSize="sm" color="gray.500">No listings found.</Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.15em" color="blue.500">
        {result.meta?.total != null ? `${result.meta.total} listing${result.meta.total !== 1 ? 's' : ''} found` : `${listings.length} listing${listings.length !== 1 ? 's' : ''}`}
      </Text>
      {listings.map((listing) => (
        <ListingCard
          key={listing.id || listing._id}
          listing={listing}
          role={role}
          navigate={navigate}
          onTriggerMessage={onTriggerMessage}
        />
      ))}
      {hasMore ? (
        <Button
          size="sm"
          variant="outline"
          colorScheme="blue"
          onClick={handleShowMore}
          isLoading={loadingMore}
        >
          Show 5 more
        </Button>
      ) : null}
    </VStack>
  );
}
